import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { Credentials, CredentialTypeModel, AddCredentialParams } from 'src/providers/credentials/credentials';
import { environment } from 'src/environments/environment';
import { MOCK_DATA_BY_KEY, MOCK_DATA_BY_INPUT_TYPE } from './mock-data.constants';

@Component({
  selector: 'mock-data-button',
  template: `
    <ng-container *ngIf="!isProduction">
      <tidy-row [align]="'center'" style="margin-bottom: 10px;">
        <tidy-text
          *ngIf="!isRunning"
          class="link"
          [actionBody]="'Fill Mock Data (DEV)'"
          (action)="fillMockData()"
          style="font-size: 11px; opacity: 0.6;">
        </tidy-text>
        <tidy-text
          *ngIf="isRunning"
          [helper]="progress"
          style="font-size: 11px;">
        </tidy-text>
      </tidy-row>
      <tidy-error-message
        *ngIf="errorMessage"
        [text]="errorMessage">
      </tidy-error-message>
    </ng-container>
  `,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class MockDataButtonComponent {
  @Input() credentialTypes: CredentialTypeModel[] = [];
  @Output() dataFilled = new EventEmitter<void>();

  isProduction = environment.production;
  isRunning = false;
  progress = '';
  errorMessage = '';

  constructor(private credentialsProvider: Credentials) {}

  async fillMockData() {
    if (this.isRunning || this.isProduction) return;

    this.isRunning = true;
    this.errorMessage = '';
    this.progress = 'Starting...';

    try {
      const supported = this.credentialTypes.filter(ct => {
        const inputType = ct.form_options?.input_type || 'file';
        return ['textarea', 'url', 'number', 'toggle', 'file'].includes(inputType)
          || ct.key === 'profile_image';
      });

      // Fisher-Yates shuffle
      const shuffled = [...supported];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      const count = Math.min(Math.max(10, Math.ceil(supported.length * 0.7)), supported.length);
      const selected = shuffled.slice(0, count);

      let successCount = 0;
      let skipCount = 0;

      for (let i = 0; i < selected.length; i++) {
        this.progress = `Filling ${i + 1}/${selected.length}...`;
        try {
          const params = this.buildParams(selected[i]);
          await this.credentialsProvider.addCredential(params);
          successCount++;
        } catch {
          skipCount++;
        }
      }

      this.progress = `Done! Filled ${successCount}${skipCount ? ', skipped ' + skipCount : ''}.`;
      this.dataFilled.emit();
    } catch (err: any) {
      this.errorMessage = err?.message || err?.error?.message || 'Mock data fill failed.';
    } finally {
      this.isRunning = false;
    }
  }

  private buildParams(ct: CredentialTypeModel): AddCredentialParams {
    const inputType = ct.form_options?.input_type || 'file';
    const effectiveType = ct.key === 'profile_image' ? 'profile_image' : inputType;
    const variants = MOCK_DATA_BY_KEY[ct.key] || MOCK_DATA_BY_INPUT_TYPE[effectiveType] || [];
    const randomVariant = variants.length ? variants[Math.floor(Math.random() * variants.length)] : '';

    const params: AddCredentialParams = {
      credential_type_key: ct.key
    };

    switch (inputType) {
      case 'textarea':
      case 'url':
        params.text_value = randomVariant;
        break;
      case 'number': {
        let num = parseInt(randomVariant, 10) || 5;
        const min = ct.form_options?.min;
        const max = ct.form_options?.max;
        if (min != null && num < min) num = min;
        if (max != null && num > max) num = max;
        params.text_value = String(num);
        break;
      }
      case 'toggle':
        params.text_value = 'true';
        break;
      case 'file':
      default:
        if (effectiveType === 'profile_image') {
          params.file_url = randomVariant || MOCK_DATA_BY_INPUT_TYPE['profile_image'][0];
        } else {
          params.file_url = randomVariant || MOCK_DATA_BY_INPUT_TYPE['file'][0];
        }
        break;
    }

    return params;
  }
}
